import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { boreholeCard, lithology, waterQualityParameters, companies, type LithologyType } from '@/api/endpoints';
import type { Company } from '@/types';
import { hasPermission } from '@/types';
import { useAuth } from '@/auth/AuthContext';
import { SearchableSelect } from '@/components/SearchableSelect';
import { PATTERNS } from '@/components/HatchingFill';
import { CardPreview } from './CardPreview';
import { ExtractionReviewModal } from './ExtractionReviewModal';
import { matchLithology } from './matchLithology';
import { validateUploadSize } from '@/lib/uploadLimits';
import { COORDINATE_SYSTEM_OPTIONS, type CoordinateSystem } from '@/lib/coordFormat';
import type {
  BoreholeCardModel, CardLayer, CardCasing, CardWaterLevel, CardPumping, CardLiquidationZone, CardRevision,
  CardWaterValue, WaterQualityParameterDto, CasingSegmentKind, WaterLevelKind, CardExtraction,
  CasingPhase, CasingDecision, LiquidationMaterial,
} from './cardTypes';

interface Props {
  open: boolean;
  boreholeId: number;
  onClose: () => void;
  onSaved?: () => void;
  /** Dev/podgląd: pomija fetch i używa podanego modelu (np. mock). */
  initialModel?: BoreholeCardModel;
  /** Render jako pełna strona (bez ciemnego overlaya) zamiast okna modalnego. */
  asPage?: boolean;
  /** Etykieta linku powrotu w trybie asPage (np. „Karty otworów"). Domyślnie „Powrót". */
  backLabel?: string;
}

const CARD_TYPES: Array<{ value: string; label: string }> = [
  { value: 'DOKUMENTACJA_WYKONANIA', label: 'Dokumentacja z wykonania' },
  { value: 'PROJEKT_NOWA', label: 'Projekt — nowy otwór' },
  { value: 'PROJEKT_LIKWIDACJA', label: 'Projekt — likwidacja' },
  { value: 'PROJEKT_REKONSTRUKCJA', label: 'Projekt — rekonstrukcja' },
  { value: 'DOKUMENTACJA_LIKWIDACJI', label: 'Dokumentacja z likwidacji' },
];
// Kolumny które można ukrywać (klucz → etykieta). Zależne od typu/rodziny — filtrowane w UI.
const COLUMN_LABELS: Record<string, string> = {
  casing: 'Konstrukcja', water: 'Zwierciadło wody', strat: 'Stratygrafia', soil: 'Kategoria gruntu',
  tools: 'Narzędzia', course: 'Przebieg robót', results: 'Wyniki wody (kol.)', uwagi: 'Uwagi',
  waterpanel: 'Panel wyników wody', difficulties: 'Utrudnienia', plannedTests: 'Przewidywane badania',
  mud: 'Rodzaj płuczki', bit: 'Rodzaj świdra', pressure: 'Nacisk', rpm: 'Obroty', flow: 'Ilość płuczki',
};
const COLUMNS_BY_TYPE = (cardType: string, family: string): string[] => {
  if (cardType === 'PROJEKT_LIKWIDACJA' || cardType === 'PROJEKT_REKONSTRUKCJA' || cardType === 'DOKUMENTACJA_LIKWIDACJI')
    return ['water', 'strat', 'uwagi'];
  if (cardType === 'PROJEKT_NOWA') return ['casing', 'water', 'strat', 'difficulties', 'plannedTests', 'mud', 'bit', 'pressure', 'rpm', 'flow', 'uwagi'];
  if (family === 'ZZWW') return ['casing', 'water', 'strat', 'soil', 'tools', 'course', 'results', 'uwagi'];
  return ['casing', 'water', 'strat', 'soil', 'tools', 'course', 'waterpanel'];
};

// ---------- helpers ----------
const num = (s: string | null | undefined): number | null => {
  if (s === null || s === undefined) return null;
  const t = s.trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};
const S = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

const CASING_KINDS: Array<{ value: CasingSegmentKind; label: string }> = [
  { value: 'DRILL_BORE', label: 'Przewiert' },
  { value: 'CASING_PIPE', label: 'Rura okładzinowa' },
  { value: 'OVER_FILTER_PIPE', label: 'Rura nadfiltrowa' },
  { value: 'FILTER', label: 'Filtr (część robocza)' },
  { value: 'UNDER_FILTER_PIPE', label: 'Rura podfiltrowa' },
  { value: 'GRAVEL_PACK', label: 'Obsypka żwirowa' },
  { value: 'CLAY_PLUG', label: 'Korek iłowy' },
  { value: 'SEAL', label: 'Zamek / uszczelka' },
  { value: 'CEMENTATION', label: 'Cementacja' },
  { value: 'SUMP', label: 'Rura-osadnik (Sumpf)' },
  { value: 'BOTTOM_CAP', label: 'Denko (Bodenkappe)' },
];
const CASING_KIND_SET = new Set<string>(CASING_KINDS.map((k) => k.value));
const CASING_PHASES = [{ value: 'PLANNED', label: 'Projekt' }, { value: 'EXISTING', label: 'Istniejący' }];
const CASING_DECISIONS = [
  { value: '', label: '—' }, { value: 'REMOVE', label: 'wyciągnąć' }, { value: 'LEAVE', label: 'pozostawić' },
  { value: 'LEAVE_BROKEN', label: 'pozostawić (zerwane)' }, { value: 'ATTEMPT_REMOVE', label: 'próba wyciągnięcia' },
];
const LIQ_MATERIALS = [
  { value: 'CEMENT_PLUG', label: 'Korek cementowy' }, { value: 'CONCRETE_PLUG', label: 'Korek betonowy' },
  { value: 'BENTONITE', label: 'Bentonit' }, { value: 'COMPACTONITE', label: 'Compactonit' },
  { value: 'CLAY_SEAL', label: 'Iłowanie' }, { value: 'GRAVEL_FILL', label: 'Żwir' },
  { value: 'SAND_BACKFILL', label: 'Samozasyp / piasek' }, { value: 'MARKER_PLATE', label: 'Świadek / płyta' },
];

// ---------- klik w podgląd → pole w edytorze ----------
// Identyfikatory sekcji (rozwijanych <details>) i mapowanie prefiksu `data-field` → sekcja.
const SECTION_IDS = ['pres', 'titleblock', 'columns', 'projekt', 'borehole', 'hydro', 'layers', 'waterlevels', 'casing', 'liquidation', 'pumping', 'water'];
const PREFIX_SECTION: Record<string, string> = {
  pres: 'pres', borehole: 'borehole', hydro: 'hydro', layer: 'layers', casing: 'casing',
  waterlevel: 'waterlevels', liq: 'liquidation', pumping: 'pumping', waterval: 'water',
  water: 'water', titleblock: 'titleblock', projekt: 'projekt', columns: 'columns',
};
/** Sekcja, którą trzeba rozwinąć dla danego identyfikatora pola (`data-field` / `data-fid`). */
const sectionFor = (fid: string): string | null =>
  SECTION_IDS.includes(fid) ? fid : (PREFIX_SECTION[fid.split('.')[0]] ?? null);
const cssEsc = (s: string): string =>
  (typeof CSS !== 'undefined' && CSS.escape) ? CSS.escape(s) : s.replace(/["\\]/g, '\\$&');
/** Krótkie podświetlenie pola po przejściu z podglądu. */
const flashEl = (el: HTMLElement) => {
  el.classList.add('fld-flash');
  window.setTimeout(() => el.classList.remove('fld-flash'), 1100);
};

// Kontekst rozwijanych sekcji edytora — pozwala kliknięciu w podgląd sterować otwarciem sekcji.
const SecCtx = createContext<{ openSet: Set<string>; toggle: (id: string, open: boolean) => void }>({
  openSet: new Set(), toggle: () => {},
});

/**
 * Przejście z karty konkretnej studni do analiz sitowych TEGO otworu
 * (moduł „Analiza sitowa / Obsypka", lista przefiltrowana po boreholeId).
 * Osobny komponent, bo tylko on dotyka AuthContext — dzięki temu podgląd deweloperski
 * (/dev/karta, bez trybu asPage) nie zaciąga hooka uprawnień.
 */
function SieveLinkForBorehole({ boreholeId }: { boreholeId: number }) {
  const { user } = useAuth();
  if (!hasPermission(user, 'SIEVE_VIEW') || !Number.isFinite(boreholeId) || boreholeId <= 0) return null;
  return (
    <NavLink to={`/analiza-sitowa?boreholeId=${boreholeId}`}
      style={{ marginLeft: 'auto', color: '#0891b2', fontSize: 12, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
      title="Analizy sitowe (uziarnienie, dobór obsypki i szczeliny filtra) wykonane dla tego otworu">
      Analizy sitowe otworu →
    </NavLink>
  );
}

/**
 * „Karta otworu" — edytor wizualny: po lewej żywy podgląd SVG (= wektorowy PDF przez FOP),
 * po prawej panel edycji sekcji. Zmiany aktualizują podgląd na bieżąco; „Zapisz kartę" utrwala
 * (PUT /card, replace-all), a „Pobierz/Zapisz PDF" transkoduje aktualny SVG.
 */
export function BoreholeCardModal({ open, boreholeId, onClose, onSaved, initialModel, asPage, backLabel }: Props) {
  const [draft, setDraft] = useState<BoreholeCardModel | null>(null);
  const [lithos, setLithos] = useState<LithologyType[]>([]);
  const [params, setParams] = useState<WaterQualityParameterDto[]>([]);
  const [companyList, setCompanyList] = useState<Company[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [mapImage, setMapImage] = useState<string | null>(null);
  const [logoImage, setLogoImage] = useState<string | null>(null);
  // propozycja odczytu AI oczekująca na zatwierdzenie (okno dialogowe) — dopiero po „Zatwierdź" scalana w draft
  const [review, setReview] = useState<CardExtraction | null>(null);
  // stan odczytu AI z dokumentu: nakładka z paskiem postępu + liczony czas (sekundy, krok 0,1 s)
  const [extracting, setExtracting] = useState(false);
  // postęp wysyłania pliku do odczytu AI (null = brak wysyłki)
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [aiElapsed, setAiElapsed] = useState(0);
  const [aiFileName, setAiFileName] = useState<string | null>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // rozwinięte sekcje edytora (domyślnie „Nagłówek / prezentacja") + oczekujące przejście do pola
  const [openSet, setOpenSet] = useState<Set<string>>(() => new Set(['pres']));
  const [pendingFocus, setPendingFocus] = useState<string | null>(null);
  const toggleSec = (id: string, isOpen: boolean) => setOpenSet((prev) => {
    const n = new Set(prev);
    if (isOpen) n.add(id); else n.delete(id);
    return n;
  });

  // Licznik czasu odczytu AI — tyka co 100 ms, dopóki trwa ekstrakcja (nakładka z postępem).
  useEffect(() => {
    if (!extracting) return;
    const t0 = Date.now();
    setAiElapsed(0);
    const iv = window.setInterval(() => setAiElapsed((Date.now() - t0) / 1000), 100);
    return () => window.clearInterval(iv);
  }, [extracting]);
  // klik w element podglądu (`data-field`) → rozwiń sekcję i zaznacz pole do sfokusowania
  const pickField = (fid: string) => {
    const sec = sectionFor(fid);
    if (sec) setOpenSet((prev) => (prev.has(sec) ? prev : new Set(prev).add(sec)));
    setPendingFocus(fid);
  };

  useLayoutEffect(() => {
    if (!open) return;
    resetDirty();
    setErr(null); setMsg(null);
    if (initialModel) { setDraft(initialModel); }
    else {
      setDraft(null);
      boreholeCard.get(boreholeId)
        .then((m) => { setDraft(m as BoreholeCardModel); resetDirty(); })
        .catch((e) => setErr(e?.message ?? 'Błąd wczytywania karty'));
    }
    lithology.list().then((l) => setLithos(l as LithologyType[])).catch(() => {});
    waterQualityParameters.list().then(setParams).catch(() => {});
    companies.list().then(setCompanyList).catch(() => {});
  }, [open, boreholeId, initialModel]);

  // miniatura mapy — po wczytaniu karty i przy zmianie skali mapy (dev/mock: 204/404 → placeholder)
  const mapScale = draft?.presentation.mapScale ?? null;
  useEffect(() => {
    if (!open || !draft) return;
    boreholeCard.mapThumbnail(boreholeId, mapScale).then(setMapImage).catch(() => setMapImage(null));
  }, [open, boreholeId, draft, mapScale]);

  // logo firmy → data URI (z context.companyLogoUrl); pobierane raz po wczytaniu
  const logoUrl = draft?.context.companyLogoUrl ?? null;
  useEffect(() => {
    if (!open || !logoUrl) { setLogoImage(null); return; }
    boreholeCard.imageDataUri(logoUrl).then(setLogoImage).catch(() => setLogoImage(null));
  }, [open, logoUrl]);

  // po wskazaniu pola z podglądu (efekt uruchamia się po rozwinięciu sekcji w DOM): przewiń + sfokusuj + podświetl
  useEffect(() => {
    if (!pendingFocus) return;
    const fid = pendingFocus;
    setPendingFocus(null);
    const panel = panelRef.current;
    if (!panel) return;
    const sec = sectionFor(fid);
    const el = panel.querySelector<HTMLElement>(`[data-fid="${cssEsc(fid)}"]`)
      ?? (sec ? panel.querySelector<HTMLElement>(`[data-sec="${cssEsc(sec)}"]`) : null);
    if (!el) return;
    if (el.hasAttribute('data-sec')) {
      // brak konkretnego pola → przewiń do sekcji i podświetl jej nagłówek
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const sum = el.querySelector<HTMLElement>('summary');
      if (sum) flashEl(sum);
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const focusable = el.matches('input,textarea,select,button')
        ? el : el.querySelector<HTMLElement>('input,textarea,select,button');
      focusable?.focus?.({ preventScroll: true });
      flashEl(el);
    }
  }, [pendingFocus]);

  const lithoOpts = useMemo(
    () => lithos.map((t) => ({ value: String(t.id), label: `${t.code} — ${t.name}` })),
    [lithos],
  );
  const paramOpts = useMemo(
    () => params.map((p) => ({ value: String(p.id), label: p.name })),
    [params],
  );

  const { requestClose, confirmation, dialogProps, resetDirty } = useUnsavedChanges({
    value: draft, onClose, open, busy: busy || extracting,
  });

  if (!open) return null;

  // ---------- mutatory ----------
  const patch = (p: Partial<BoreholeCardModel>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const patchPres = (p: Partial<BoreholeCardModel['presentation']>) =>
    setDraft((d) => (d ? { ...d, presentation: { ...d.presentation, ...p } } : d));

  // Wybór firmy/nagłówka (jak w ofertach). Aktualizuje kontekst nagłówka (nazwa/adres/NIP/kontakt/logo)
  // od razu, by podgląd SVG i logo odświeżyły się na żywo; „" (null) = firma domyślna systemu.
  const companyAddr = (co: Company): string | null => {
    const city = [co.addressPostalCode, co.addressCity].filter(Boolean).join(' ').trim();
    return [co.addressLine1, city].filter((s) => s && s.trim()).join(', ') || null;
  };
  const companyContactLine = (co: Company): string | null =>
    [co.phone ? `tel. ${co.phone}` : null, co.email, co.website].filter(Boolean).join(' · ') || null;
  const pickCompany = (v: string) => {
    const id = v ? Number(v) : null;
    // „auto" (null) → podgląd pokazuje firmę domyślną z listy
    const co = (id ? companyList.find((c) => c.id === id) : companyList.find((c) => c.isDefault)) ?? null;
    setDraft((d) => d ? {
      ...d,
      presentation: { ...d.presentation, companyId: id },
      context: {
        ...d.context,
        companyName: co ? (co.fullName || co.shortName) : d.context.companyName,
        companyAddress: co ? companyAddr(co) : d.context.companyAddress,
        companyNip: co ? (co.nip ?? null) : d.context.companyNip,
        companyContact: co ? companyContactLine(co) : d.context.companyContact,
        companyLogoUrl: co ? (co.logoUrl ?? null) : null,
      },
    } : d);
  };
  const setLayers = (fn: (a: CardLayer[]) => CardLayer[]) =>
    setDraft((d) => (d ? { ...d, layers: fn(d.layers) } : d));
  const setCasing = (fn: (a: CardCasing[]) => CardCasing[]) =>
    setDraft((d) => (d ? { ...d, casing: fn(d.casing) } : d));
  const setLiq = (fn: (a: CardLiquidationZone[]) => CardLiquidationZone[]) =>
    setDraft((d) => (d ? { ...d, liquidation: fn(d.liquidation) } : d));
  const setRev = (fn: (a: CardRevision[]) => CardRevision[]) =>
    setDraft((d) => (d ? { ...d, revisions: fn(d.revisions) } : d));
  const setWls = (fn: (a: CardWaterLevel[]) => CardWaterLevel[]) =>
    setDraft((d) => (d ? { ...d, waterLevels: fn(d.waterLevels) } : d));
  const setPump = (fn: (a: CardPumping[]) => CardPumping[]) =>
    setDraft((d) => (d ? { ...d, pumping: fn(d.pumping) } : d));
  const setWater = (fn: (w: BoreholeCardModel['water']) => BoreholeCardModel['water']) =>
    setDraft((d) => (d ? { ...d, water: fn(d.water) } : d));

  const upd = <T,>(arr: T[], i: number, p: Partial<T>): T[] => arr.map((x, j) => (j === i ? { ...x, ...p } : x));
  const del = <T,>(arr: T[], i: number): T[] => arr.filter((_, j) => j !== i);

  // przelicz sugestie hydro (q=Q/s per stopień, k[m/h], Sichardt R)
  const recompute = () => setDraft((d) => {
    if (!d) return d;
    const pumping = d.pumping.map((s) => ({
      ...s,
      specificCapacity: s.qM3h != null && s.drawdownSM != null && s.drawdownSM !== 0
        ? Math.round((s.qM3h / s.drawdownSM) * 10000) / 10000 : s.specificCapacity,
    }));
    const p = d.presentation;
    const kmh = p.kValueMs != null ? Math.round(p.kValueMs * 3600 * 1e6) / 1e6 : p.kValueMh;
    const s = p.scTotalM ?? Math.max(0, ...d.pumping.map((x) => x.drawdownSM ?? 0));
    const r = p.kValueMs != null && s > 0
      ? Math.round(575 * s * Math.sqrt(p.kValueMs) * 100) / 100 : p.rRangeM;
    return { ...d, pumping, presentation: { ...p, kValueMh: kmh, rRangeM: r } };
  });

  const pickLitho = (i: number, id: string) => {
    const t = lithos.find((x) => String(x.id) === id);
    setLayers((a) => a.map((l, j) => j === i ? {
      ...l,
      lithologyTypeId: t ? t.id : null,
      color: t ? t.color : l.color,
      pattern: t ? t.pattern : l.pattern,
      symbol: l.symbol || (t ? t.code : l.symbol),
      description: l.description || (t ? t.name : l.description),
    } : l));
  };

  const addParamRow = (id: string) => {
    const p = params.find((x) => String(x.id) === id);
    if (!p) return;
    setWater((w) => {
      const base = w ?? { id: null, sampledDate: null, labName: null, examiner: null, remarks: null, values: [] };
      const nv: CardWaterValue = { id: null, parameterId: p.id, paramName: p.name, unit: p.unit, valueText: null, sortOrder: (base.values.length + 1) * 10 };
      return { ...base, values: [...base.values, nv] };
    });
  };

  // ---------- prefill AI: scalanie propozycji w draft (nadpisuje tylko pola/kolekcje znalezione) ----------
  const applyExtraction = (x: CardExtraction) => setDraft((d) => {
    if (!d) return d;
    const K = <T,>(cur: T, v: T | null | undefined): T => (v === null || v === undefined ? cur : v);
    const layers = x.layers?.length ? x.layers.map((l) => {
      const m = matchLithology(l.symbol, l.description, lithos);
      return {
        id: null, fromDepth: l.fromDepth, toDepth: l.toDepth,
        symbol: l.symbol ?? (m?.code ?? ''), description: l.description ?? (m?.name ?? ''),
        color: m?.color ?? null, pattern: m?.pattern ?? null, lithologyTypeId: m?.lithologyTypeId ?? null,
        stratigraphy: l.stratigraphy ?? null, soilCategory: l.soilCategory ?? null, facialType: null, remarks: null,
      };
    }) : d.layers;
    const waterLevels = x.waterLevels?.length ? x.waterLevels.map((w) => ({
      id: null, kind: (w.kind === 'DRILLED' ? 'DRILLED' : 'STABILIZED') as WaterLevelKind,
      depthMPpt: w.depthMPpt, measuredDate: w.measuredDate, aquiferNote: null, remarks: null,
    })) : d.waterLevels;
    const casing = x.casing?.length ? x.casing.map((c, i) => ({
      id: null, kind: (CASING_KIND_SET.has(c.kind ?? '') ? c.kind : 'CASING_PIPE') as CasingSegmentKind,
      phase: 'PLANNED' as const, decision: null, piezometer: false,
      fromDepth: c.fromDepth, toDepth: c.toDepth, outerDiameterMm: c.outerDiameterMm, innerDiameterMm: null,
      diameterInch: c.diameterInch, material: c.material, meshType: c.meshType, fraction: c.fraction,
      label: null, sortOrder: (i + 1) * 10, remarks: null,
    })) : d.casing;
    const pumping = x.pumping?.length ? x.pumping.map((p, i) => ({
      id: null, stageNo: p.stageNo ?? i + 1, qM3h: p.qM3h, drawdownSM: p.drawdownSM,
      durationH: p.durationH, specificCapacity: p.specificCapacity, remarks: null,
    })) : d.pumping;
    const water = x.water && x.water.values?.length ? {
      id: null, sampledDate: x.water.sampledDate, labName: x.water.labName, examiner: null, remarks: null,
      values: x.water.values.map((v, i) => ({ id: null, parameterId: null, paramName: v.paramName ?? '', unit: v.unit, valueText: v.valueText, sortOrder: (i + 1) * 10 })),
    } : d.water;
    return {
      ...d,
      elevationMNpm: K(d.elevationMNpm, x.elevationMNpm), workStartDate: K(d.workStartDate, x.workStartDate), workEndDate: K(d.workEndDate, x.workEndDate),
      drillingSystem: K(d.drillingSystem, x.drillingSystem), drillingTools: K(d.drillingTools, x.drillingTools), drillingCourse: K(d.drillingCourse, x.drillingCourse),
      sampleMethod: K(d.sampleMethod, x.sampleMethod), sampleStorage: K(d.sampleStorage, x.sampleStorage),
      totalDepth: K(d.totalDepth, x.totalDepth), diameterMm: K(d.diameterMm, x.diameterMm), yieldM3h: K(d.yieldM3h, x.yieldM3h), yearCompleted: K(d.yearCompleted, x.yearCompleted),
      presentation: {
        ...d.presentation,
        kValueMs: K(d.presentation.kValueMs, x.kValueMs), kFormula: K(d.presentation.kFormula, x.kFormula),
        rRangeM: K(d.presentation.rRangeM, x.rRangeM), rFormula: K(d.presentation.rFormula, x.rFormula),
        qExploatacyjneM3h: K(d.presentation.qExploatacyjneM3h, x.qExploatacyjneM3h), qDopFiltraM3h: K(d.presentation.qDopFiltraM3h, x.qDopFiltraM3h),
        scTotalM: K(d.presentation.scTotalM, x.scTotalM), documentingGeologistText: K(d.presentation.documentingGeologistText, x.documentingGeologistText),
      },
      layers, waterLevels, casing, pumping, water,
    };
  });

  const onExtractFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    // Limit rozmiaru pliku (100 MB) — komunikat gotowy do pokazania.
    const sizeErr = validateUploadSize(file);
    if (sizeErr) { setMsg('Błąd AI: ' + sizeErr); return; }
    // Zeruj licznik synchronicznie (nie tylko w efekcie po paincie) — inaczej przy 2. odczycie
    // nakładka mignęłaby czasem z poprzedniego przebiegu i paskiem ~96%.
    setBusy(true); setAiElapsed(0); setExtracting(true); setAiFileName(file.name); setMsg(null); setUploadPct(0);
    try {
      const x = await boreholeCard.extract(boreholeId, file, p => setUploadPct(p.percent));
      // Nie scalaj od razu — pokaż okno zatwierdzenia z możliwością edycji odczytanych danych.
      setReview(x);
      setMsg(null);
    } catch (err) { setMsg('Błąd AI: ' + ((err as Error)?.message ?? '')); }
    finally { setBusy(false); setExtracting(false); setUploadPct(null); }
  };

  // Zatwierdzenie odczytu z okna dialogowego — dopiero teraz scalamy w draft karty.
  const onReviewConfirm = (edited: CardExtraction) => {
    applyExtraction(edited);
    setReview(null);
    const n = edited.extractedFields?.length ?? 0;
    setMsg(`Wypełniono kartę z odczytu (${n} pól) — sprawdź podgląd i „Zapisz kartę".`);
  };

  const serializeSvg = (): string | null => {
    const svg = frameRef.current?.querySelector('svg');
    return svg ? new XMLSerializer().serializeToString(svg) : null;
  };

  // Pobranie samodzielnego pliku SVG (logo/mapa są osadzone jako data-URI, szrafury w <defs> — plik jest kompletny).
  const downloadSvg = () => {
    const svg = serializeSvg();
    if (!svg) { setMsg('Brak podglądu do pobrania.'); return; }
    const doc = svg.startsWith('<?xml') ? svg : `<?xml version="1.0" encoding="UTF-8"?>\n${svg}`;
    const url = URL.createObjectURL(new Blob([doc], { type: 'image/svg+xml;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `karta-otworu-${(draft?.designation || 'otwor').replace(/[^\w.-]+/g, '_')}.svg`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    setMsg('Pobrano SVG.');
  };

  const saveCard = async () => {
    if (!draft) return;
    setBusy(true); setMsg(null);
    try {
      const saved = await boreholeCard.save(boreholeId, toRequest(draft));
      setDraft(saved as BoreholeCardModel);
      resetDirty();
      setMsg('Zapisano kartę.');
      onSaved?.();
    } catch (e) { setMsg('Błąd zapisu karty: ' + ((e as Error)?.message ?? '')); }
    finally { setBusy(false); }
  };
  const download = async () => {
    const svg = serializeSvg(); if (!svg) return;
    setBusy(true); setMsg(null);
    try {
      const blob = await boreholeCard.renderPdf(boreholeId, svg);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { setMsg('Błąd PDF: ' + ((e as Error)?.message ?? '')); }
    finally { setBusy(false); }
  };
  const savePdf = async () => {
    const svg = serializeSvg(); if (!svg) return;
    setBusy(true); setMsg(null);
    try {
      const res = await boreholeCard.savePdf(boreholeId, svg);
      setMsg('Zapisano PDF do archiwum: ' + res.name); onSaved?.();
    } catch (e) { setMsg('Błąd zapisu PDF: ' + ((e as Error)?.message ?? '')); }
    finally { setBusy(false); }
  };

  const ct = draft?.presentation.cardType ?? 'DOKUMENTACJA_WYKONANIA';
  const isLikRek = ct === 'PROJEKT_LIKWIDACJA' || ct === 'PROJEKT_REKONSTRUKCJA' || ct === 'DOKUMENTACJA_LIKWIDACJI';
  const isLikwidacja = ct === 'PROJEKT_LIKWIDACJA' || ct === 'DOKUMENTACJA_LIKWIDACJI';

  return (
    <div style={asPage
      ? { display: 'flex', height: 'calc(100vh - 120px)' }
      : { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 60, display: 'flex', padding: 12 }}
      {...dialogProps} role={asPage ? undefined : 'dialog'} aria-modal={asPage ? undefined : true}>
      {confirmation}
      <div style={{ position: 'relative', background: '#fff', borderRadius: asPage ? 8 : 10, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', border: asPage ? '1px solid #e2e8f0' : undefined }}>
        {extracting && <AiExtractOverlay elapsed={aiElapsed} fileName={aiFileName} uploadPct={uploadPct} />}
        <div style={{ padding: asPage ? '10px 16px' : '8px 14px', borderBottom: '1px solid #e2e8f0' }}>
          {/* Pasek nawigacyjny (tylko asPage): powrót po lewej, przejścia do powiązanych modułów po prawej.
              Leży nad edytorem, poza obszarem CardPreview, więc nie zmienia układu karty ani wydruku/PDF. */}
          {asPage && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
              <button onClick={requestClose}
                style={{ background: 'none', border: 'none', color: '#0891b2', fontSize: 12, cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                ← {backLabel ?? 'Powrót'}
              </button>
              <SieveLinkForBorehole boreholeId={boreholeId} />
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: asPage ? 16 : 15, fontWeight: 600, color: '#0f172a' }}>
                Karta otworu{draft?.designation ? ` — ${draft.designation}` : ''}
              </div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                {draft?.context?.intakeName
                  ? `Ujęcie ${draft.context.intakeName}${draft.context.clientName ? ' · ' + draft.context.clientName : ''}`
                  : 'Zestawienie wyników wiercenia — edytor'}
              </div>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
              {msg && <span style={{ fontSize: 12, color: msg.startsWith('Błąd') ? '#b91c1c' : '#15803d' }}>{msg}</span>}
              {/* Jawna lista formatów (bez `image/*`, który wpuszczał też TIFF/BMP/SVG nieobsługiwane przez vision). */}
              <input ref={fileRef} type="file"
                accept=".png,.jpg,.jpeg,.webp,.gif,.heic,.heif,.pdf,.doc,.docx,.xlsx,.xls" style={{ display: 'none' }} onChange={onExtractFile} />
              <button onClick={() => fileRef.current?.click()} disabled={busy || !draft} style={btnOutline('#b45309')}
                title="Wgraj zdjęcie, PDF, dokument lub arkusz Excel — AI odczyta dane i pokaże do zatwierdzenia">{extracting ? `AI odczytuje… (${Math.round(aiElapsed)} s)` : busy ? 'Pracuję…' : '✦ Wypełnij z dokumentu (AI)'}</button>
              <button onClick={saveCard} disabled={busy || !draft} style={btn('#15803d')}>Zapisz kartę</button>
              <ExportMenu
                disabled={busy || !draft}
                onPdf={download}
                onSvg={downloadSvg}
                onSaveToSystem={savePdf}
              />
              {!asPage && <button onClick={requestClose} style={btn('#475569')}>Zamknij</button>}
            </div>
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* podgląd (przybliżanie + minimapa) */}
          {err
            ? <div style={{ flex: 1, background: '#e2e8f0', padding: 16, color: '#b91c1c' }}>{err}</div>
            : !draft
              ? <div style={{ flex: 1, background: '#e2e8f0', padding: 16, color: '#64748b' }}>Wczytywanie…</div>
              : <CardPreview model={draft} mapImage={mapImage} logoImage={logoImage} frameRef={frameRef} onFieldPick={pickField} />}

          {/* edytor */}
          {draft && (
            <div ref={panelRef} style={{ width: 420, minWidth: 420, overflow: 'auto', borderLeft: '1px solid #e2e8f0', padding: 12, fontSize: 12 }}>
              <style>{`
                @keyframes fldFlash { 0%,100% { box-shadow: none } 25% { box-shadow: 0 0 0 3px rgba(8,145,178,0.45) } }
                .fld-flash { animation: fldFlash 1.1s ease; border-radius: 5px }
                .fl-hint { color: #94a3b8 }
              `}</style>
              <SecCtx.Provider value={{ openSet, toggle: toggleSec }}>
              <Sec id="pres" title="Nagłówek / prezentacja">
                <Stack>
                  <FSelect label="Typ karty" value={draft.presentation.cardType} onChange={(v) => patchPres({ cardType: v as BoreholeCardModel['presentation']['cardType'], titleVariant: null, hiddenColumns: null })} options={CARD_TYPES} searchThreshold={20} />
                  {!draft.presentation.cardType.startsWith('PROJEKT') && (
                    <FSelect label="Szablon" value={draft.presentation.templateFamily} onChange={(v) => patchPres({ templateFamily: v as 'MODERN' | 'ZZWW' })} options={[{ value: 'MODERN', label: 'Nowoczesny (panel wody z boku)' }, { value: 'ZZWW', label: 'Zbiorcze zestawienie (ZZWW)' }]} searchThreshold={20} />
                  )}
                  <FTxt label="Nr załącznika" fid="pres.attachmentLabel" v={draft.presentation.attachmentLabel} set={(v) => patchPres({ attachmentLabel: v })} />
                  <FTxt label="Tytuł (override)" fid="pres.titleVariant" v={draft.presentation.titleVariant} set={(v) => patchPres({ titleVariant: v })} />
                  <FTxt label="Skala mapy" fid="pres.mapScale" v={draft.presentation.mapScale} set={(v) => patchPres({ mapScale: v })} />
                  <FTxt label="Skala głębok." v={draft.presentation.depthScale} set={(v) => patchPres({ depthScale: v })} />
                  <FTxt label="Wystawka rury +[m]" fid="pres.casingStickupM" v={draft.presentation.casingStickupM} set={(v) => patchPres({ casingStickupM: num(v) })} />
                  <FSelect label="Układ współrz. (nagłówek)" fid="pres.coordinateSystem"
                    value={draft.presentation.coordinateSystem ?? 'WGS84'}
                    onChange={(v) => patchPres({ coordinateSystem: v as CoordinateSystem })}
                    options={COORDINATE_SYSTEM_OPTIONS} searchThreshold={20} />
                  <FTxt label="Geolog dokum." fid="pres.geologist" v={draft.presentation.documentingGeologistText ?? draft.presentation.geologistResolvedName} set={(v) => patchPres({ documentingGeologistText: v })} />
                  <FTxt label="Drugi podpis" v={draft.presentation.coSignerText} set={(v) => patchPres({ coSignerText: v })} />
                  <FSelect label="Firma / nagłówek" fid="pres.company" value={S(draft.presentation.companyId)} onChange={pickCompany} searchThreshold={6}
                    options={[{ value: '', label: 'Domyślna firmy (auto)' }, ...companyList.map((c) => ({ value: String(c.id), label: c.shortName + (c.logoUrl ? '' : ' — brak logo') }))]} />
                  {draft.presentation.showLogo !== false && !draft.context.companyLogoUrl && (
                    <div style={{ fontSize: 11, color: '#b45309' }}>
                      Wybrana firma nie ma wgranego logo — dodaj je w Administracja → Firmy (nagłówki).
                    </div>
                  )}
                  <Fld l="Logo firmy"><label style={{ fontSize: 12 }}><input type="checkbox" checked={draft.presentation.showLogo !== false} onChange={(e) => patchPres({ showLogo: e.target.checked })} /> pokaż logo i dane firmy</label></Fld>
                </Stack>
              </Sec>

              <Sec id="titleblock" title="Metryczka rysunku">
                <Stack>
                  <Fld l="Pokaż metryczkę"><label style={{ fontSize: 12 }}><input type="checkbox" checked={draft.presentation.showTitleBlock === true} onChange={(e) => patchPres({ showTitleBlock: e.target.checked })} /> tabela rewizji + podpisy na dole</label></Fld>
                  <FTxt label="Tytuł rysunku" v={draft.presentation.drawingTitle} set={(v) => patchPres({ drawingTitle: v })} />
                  <FTxt label="Sprawdził" v={draft.presentation.checkedByText} set={(v) => patchPres({ checkedByText: v })} />
                  {draft.revisions.map((r, i) => (
                    <div key={r.id ?? 'r' + i} style={rowLine}>
                      <FTxt label="indeks" w={56} v={r.revIndex} set={(v) => setRev((a) => upd(a, i, { revIndex: v }))} />
                      <FDte label="data" v={r.revDate} set={(v) => setRev((a) => upd(a, i, { revDate: v }))} />
                      <FTxt label="zmiana" v={r.changeNote} set={(v) => setRev((a) => upd(a, i, { changeNote: v }))} />
                      <button onClick={() => setRev((a) => del(a, i))} style={xbtn}>×</button>
                    </div>
                  ))}
                  <button onClick={() => setRev((a) => [...a, { id: null, revIndex: String(a.length), revDate: null, changeNote: null, sortOrder: (a.length + 1) * 10 }])} style={addBtn}>+ rewizja</button>
                </Stack>
              </Sec>

              <Sec id="columns" title="Widoczność kolumn">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px' }}>
                  {COLUMNS_BY_TYPE(draft.presentation.cardType, draft.presentation.templateFamily).map((key) => {
                    const hiddenSet = new Set(draft.presentation.hiddenColumns ?? []);
                    return (
                      <label key={key} style={{ fontSize: 11, width: '46%' }}>
                        <input type="checkbox" checked={!hiddenSet.has(key)} onChange={(e) => {
                          const next = new Set(hiddenSet);
                          if (e.target.checked) next.delete(key); else next.add(key);
                          patchPres({ hiddenColumns: [...next] });
                        }} /> {COLUMN_LABELS[key] ?? key}
                      </label>
                    );
                  })}
                </div>
              </Sec>

              {draft.presentation.cardType.startsWith('PROJEKT') && (
                <Sec id="projekt" title="Projekt (PGTO)">
                  <Stack>
                    <FTxt label="Proj. głębokość [m]" v={draft.presentation.projectedDepthM} set={(v) => patchPres({ projectedDepthM: num(v) })} />
                    <FTxt label="Cel wiercenia" v={draft.presentation.drillingPurpose} set={(v) => patchPres({ drillingPurpose: v })} />
                    <FTxt label="Zagr. erupcyjne" v={draft.presentation.eruptionHazardClass} set={(v) => patchPres({ eruptionHazardClass: v })} />
                    <FTxt label="Zagr. H₂S" v={draft.presentation.h2sHazardCategory} set={(v) => patchPres({ h2sHazardCategory: v })} />
                    <FTxt label="Odwoł. do PRG" v={draft.presentation.sourceProjectRef} set={(v) => patchPres({ sourceProjectRef: v })} />
                    <FArea label="Blok wiertni" v={draft.presentation.rigInfo} set={(v) => patchPres({ rigInfo: v })} />
                    <FArea label="Zalec. dla dozoru" fid="projekt.supervisionNotes" v={draft.presentation.supervisionNotes} set={(v) => patchPres({ supervisionNotes: v })} />
                    <FTxt label="Rodzaj płuczki" fid="projekt.mudType" v={draft.presentation.mudType} set={(v) => patchPres({ mudType: v })} />
                    <FTxt label="Rodzaj świdra" fid="projekt.bitType" v={draft.presentation.bitType} set={(v) => patchPres({ bitType: v })} />
                    <FTxt label="Nacisk [t]" fid="projekt.drillPressure" v={draft.presentation.drillPressure} set={(v) => patchPres({ drillPressure: v })} />
                    <FTxt label="Obroty" fid="projekt.drillRpm" v={draft.presentation.drillRpm} set={(v) => patchPres({ drillRpm: v })} />
                    <FTxt label="Ilość płuczki" fid="projekt.mudFlow" v={draft.presentation.mudFlow} set={(v) => patchPres({ mudFlow: v })} />
                    <FArea label="Przewidz. badania" fid="projekt.plannedTests" v={draft.presentation.plannedTests} set={(v) => patchPres({ plannedTests: v })} />
                  </Stack>
                </Sec>
              )}

              <Sec id="borehole" title="Dane otworu">
                <Stack>
                  <FTxt label="Oznaczenie" fid="borehole.designation" v={draft.designation} set={(v) => patch({ designation: v })} />
                  <FTxt label="Głęb. całk. [m]" fid="borehole.totalDepth" v={draft.totalDepth} set={(v) => patch({ totalDepth: num(v) })} />
                  <FTxt label="Średnica [mm]" v={draft.diameterMm} set={(v) => patch({ diameterMm: num(v) })} />
                  <FTxt label="Wydajność [m³/h]" v={draft.yieldM3h} set={(v) => patch({ yieldM3h: num(v) })} />
                  <FTxt label="Rzędna [m n.p.m.]" fid="borehole.elevationMNpm" v={draft.elevationMNpm} set={(v) => patch({ elevationMNpm: num(v) })} />
                  <FTxt label="Szer. (φ)" fid="borehole.latitude" v={draft.latitude} set={(v) => patch({ latitude: num(v) })} />
                  <FTxt label="Dług. (λ)" v={draft.longitude} set={(v) => patch({ longitude: num(v) })} />
                  <FDte label="Roboty od" fid="borehole.workStartDate" v={draft.workStartDate} set={(v) => patch({ workStartDate: v })} />
                  <FDte label="Roboty do" v={draft.workEndDate} set={(v) => patch({ workEndDate: v })} />
                  <FTxt label="System wierc." fid="borehole.drillingSystem" v={draft.drillingSystem} set={(v) => patch({ drillingSystem: v })} />
                  <FArea label="Narzędzia (k.9)" fid="borehole.drillingTools" v={draft.drillingTools} set={(v) => patch({ drillingTools: v })} />
                  <FArea label="Przebieg (k.10)" fid="borehole.drillingCourse" v={draft.drillingCourse} set={(v) => patch({ drillingCourse: v })} />
                  <FTxt label="Pobór próbek" fid="borehole.sampleMethod" v={draft.sampleMethod} set={(v) => patch({ sampleMethod: v })} />
                  <FTxt label="Przechow. próbek" fid="borehole.sampleStorage" v={draft.sampleStorage} set={(v) => patch({ sampleStorage: v })} />
                </Stack>
              </Sec>

              <Sec id="hydro" title="Obliczenia hydrogeologiczne">
                <div style={{ marginBottom: 8 }}><button onClick={recompute} style={btn('#0891b2')}>Przelicz (q, k[m/h], Sichardt R)</button></div>
                <Stack>
                  <FTxt label="k [m/s]" fid="hydro.kValueMs" v={draft.presentation.kValueMs} set={(v) => patchPres({ kValueMs: num(v) })} />
                  <FTxt label="k [m/h]" v={draft.presentation.kValueMh} set={(v) => patchPres({ kValueMh: num(v) })} />
                  <FTxt label="Wzór k" v={draft.presentation.kFormula} set={(v) => patchPres({ kFormula: v })} />
                  <FTxt label="R [m]" v={draft.presentation.rRangeM} set={(v) => patchPres({ rRangeM: num(v) })} />
                  <FTxt label="Wzór R" v={draft.presentation.rFormula} set={(v) => patchPres({ rFormula: v })} />
                  <FTxt label="Q ekspl. [m³/h]" v={draft.presentation.qExploatacyjneM3h} set={(v) => patchPres({ qExploatacyjneM3h: num(v) })} />
                  <FTxt label="Q dop. filtra" v={draft.presentation.qDopFiltraM3h} set={(v) => patchPres({ qDopFiltraM3h: num(v) })} />
                  <FTxt label="Sc [m]" v={draft.presentation.scTotalM} set={(v) => patchPres({ scTotalM: num(v) })} />
                  <FArea label="Uwagi hydro" v={draft.presentation.hydroNotes} set={(v) => patchPres({ hydroNotes: v })} />
                </Stack>
              </Sec>

              <Sec id="layers" title={`Profil litologiczny (${draft.layers.length})`}>
                {draft.layers.map((l, i) => (
                  <div key={l.id ?? 'l' + i} style={rowBox}>
                    <div style={rowLine}>
                      <FTxt label="od" w={44} v={l.fromDepth} set={(v) => setLayers((a) => upd(a, i, { fromDepth: num(v) }))} />
                      <FTxt label="do" w={44} v={l.toDepth} set={(v) => setLayers((a) => upd(a, i, { toDepth: num(v) }))} />
                      <FSelect label="litologia" value={S(l.lithologyTypeId)} onChange={(v) => pickLitho(i, v)} options={lithoOpts} searchThreshold={6} />
                      <button onClick={() => setLayers((a) => del(a, i))} style={xbtn}>×</button>
                    </div>
                    <FTxt label="opis warstwy" fid={`layer.${i}.desc`} v={l.description} set={(v) => setLayers((a) => upd(a, i, { description: v }))} />
                    <div style={rowLine}>
                      <FTxt label="stratygrafia" fid={`layer.${i}.strat`} v={l.stratigraphy} set={(v) => setLayers((a) => upd(a, i, { stratigraphy: v }))} />
                      <FTxt label="kat." w={54} v={l.soilCategory} set={(v) => setLayers((a) => upd(a, i, { soilCategory: v }))} />
                    </div>
                    <div style={rowLine}>
                      <FSelect label="wzór" value={S(l.pattern)} onChange={(v) => setLayers((a) => upd(a, i, { pattern: v }))} options={PATTERNS.map((p) => ({ value: p.value, label: p.label }))} searchThreshold={20} />
                      <input type="color" value={l.color ?? '#cbd5e1'} onChange={(e) => setLayers((a) => upd(a, i, { color: e.target.value }))} style={{ width: 32, height: 26, padding: 0, border: '1px solid #cbd5e1' }} />
                    </div>
                  </div>
                ))}
                <button onClick={() => setLayers((a) => [...a, { id: null, fromDepth: a.length ? a[a.length - 1].toDepth : 0, toDepth: null, symbol: '', description: '', color: '#cbd5e1', pattern: 'SOLID', lithologyTypeId: null, stratigraphy: a.length ? a[a.length - 1].stratigraphy : null, soilCategory: null, facialType: null, remarks: null }])} style={addBtn}>+ warstwa</button>
              </Sec>

              <Sec id="waterlevels" title={`Zwierciadła wody (${draft.waterLevels.length})`}>
                {draft.waterLevels.map((wl, i) => (
                  <div key={wl.id ?? 'w' + i} style={levelRow}>
                    <FSelect label="rodzaj" value={wl.kind} onChange={(v) => setWls((a) => upd(a, i, { kind: v as WaterLevelKind }))} options={[{ value: 'DRILLED', label: 'nawiercone' }, { value: 'STABILIZED', label: 'ustabiliz.' }]} searchThreshold={20} />
                    <FTxt label="głęb." w={64} fid={`waterlevel.${i}.depth`} v={wl.depthMPpt} set={(v) => setWls((a) => upd(a, i, { depthMPpt: num(v) }))} />
                    <FDte label="data" v={wl.measuredDate} set={(v) => setWls((a) => upd(a, i, { measuredDate: v }))} />
                    <button onClick={() => setWls((a) => del(a, i))} style={xbtn}>×</button>
                  </div>
                ))}
                <button onClick={() => setWls((a) => [...a, { id: null, kind: 'STABILIZED', depthMPpt: null, measuredDate: null, aquiferNote: null, remarks: null }])} style={addBtn}>+ zwierciadło</button>
              </Sec>

              <Sec id="casing" title={`Konstrukcja studni (${draft.casing.length})`}>
                {draft.casing.map((c, i) => (
                  <div key={c.id ?? 'c' + i} style={rowBox}>
                    <div style={rowLine}>
                      <FSelect label="rodzaj segmentu" value={c.kind} onChange={(v) => setCasing((a) => upd(a, i, { kind: v as CasingSegmentKind }))} options={CASING_KINDS} searchThreshold={20} />
                      <label style={{ fontSize: 10, whiteSpace: 'nowrap' }}><input type="checkbox" checked={!!c.piezometer} onChange={(e) => setCasing((a) => upd(a, i, { piezometer: e.target.checked }))} /> piez.</label>
                      <button onClick={() => setCasing((a) => del(a, i))} style={xbtn}>×</button>
                    </div>
                    {isLikRek && (
                      <div style={rowLine}>
                        <FSelect label="faza" value={c.phase ?? 'PLANNED'} onChange={(v) => setCasing((a) => upd(a, i, { phase: v as CasingPhase }))} options={CASING_PHASES} searchThreshold={20} />
                        <FSelect label="decyzja" value={c.decision ?? ''} onChange={(v) => setCasing((a) => upd(a, i, { decision: (v || null) as CasingDecision | null }))} options={CASING_DECISIONS} searchThreshold={20} />
                      </div>
                    )}
                    <div style={rowLine}>
                      <FTxt label="od" w={44} fid={`casing.${i}.from`} v={c.fromDepth} set={(v) => setCasing((a) => upd(a, i, { fromDepth: num(v) }))} />
                      <FTxt label="do" w={44} v={c.toDepth} set={(v) => setCasing((a) => upd(a, i, { toDepth: num(v) }))} />
                      <FTxt label="Ø mm" w={60} v={c.outerDiameterMm} set={(v) => setCasing((a) => upd(a, i, { outerDiameterMm: num(v) }))} />
                      <FTxt label="materiał" v={c.material} set={(v) => setCasing((a) => upd(a, i, { material: v }))} />
                    </div>
                    <div style={rowLine}>
                      <FTxt label="siatka" v={c.meshType} set={(v) => setCasing((a) => upd(a, i, { meshType: v }))} />
                      <FTxt label="frakcja obsypki" v={c.fraction} set={(v) => setCasing((a) => upd(a, i, { fraction: v }))} />
                    </div>
                  </div>
                ))}
                <button onClick={() => setCasing((a) => [...a, { id: null, kind: 'CASING_PIPE', phase: isLikRek ? 'EXISTING' : 'PLANNED', decision: null, piezometer: false, fromDepth: 0, toDepth: null, outerDiameterMm: null, innerDiameterMm: null, diameterInch: null, material: null, meshType: null, fraction: null, label: null, sortOrder: (a.length + 1) * 10, remarks: null }])} style={addBtn}>+ segment</button>
              </Sec>

              {isLikwidacja && (
                <Sec id="liquidation" title={`Strefy likwidacji (${draft.liquidation.length})`}>
                  {draft.liquidation.map((z, i) => (
                    <div key={z.id ?? 'z' + i} style={rowBox}>
                      <div style={rowLine}>
                        <FSelect label="materiał" value={z.material ?? ''} onChange={(v) => setLiq((a) => upd(a, i, { material: v as LiquidationMaterial }))} options={LIQ_MATERIALS} searchThreshold={20} />
                        <button onClick={() => setLiq((a) => del(a, i))} style={xbtn}>×</button>
                      </div>
                      <div style={rowLine}>
                        <FTxt label="od" w={44} fid={`liq.${i}.from`} v={z.fromDepth} set={(v) => setLiq((a) => upd(a, i, { fromDepth: num(v) }))} />
                        <FTxt label="do" w={44} v={z.toDepth} set={(v) => setLiq((a) => upd(a, i, { toDepth: num(v) }))} />
                        <label style={{ fontSize: 11 }}><input type="checkbox" checked={!!z.disinfectant} onChange={(e) => setLiq((a) => upd(a, i, { disinfectant: e.target.checked }))} /> chlorowanie</label>
                      </div>
                      <FTxt label="uwaga / opis" v={z.note} set={(v) => setLiq((a) => upd(a, i, { note: v }))} />
                    </div>
                  ))}
                  <button onClick={() => setLiq((a) => [...a, { id: null, material: 'CEMENT_PLUG', fromDepth: null, toDepth: null, disinfectant: false, note: null, sortOrder: (a.length + 1) * 10 }])} style={addBtn}>+ strefa</button>
                </Sec>
              )}

              <Sec id="pumping" title={`Pompowanie (${draft.pumping.length})`}>
                {draft.pumping.map((s, i) => (
                  <div key={s.id ?? 'p' + i} style={levelRow}>
                    <FTxt label="nr" w={40} v={s.stageNo} set={(v) => setPump((a) => upd(a, i, { stageNo: num(v) }))} />
                    <FTxt label="Q" w={56} v={s.qM3h} set={(v) => setPump((a) => upd(a, i, { qM3h: num(v) }))} />
                    <FTxt label="s" w={48} v={s.drawdownSM} set={(v) => setPump((a) => upd(a, i, { drawdownSM: num(v) }))} />
                    <FTxt label="t[h]" w={48} v={s.durationH} set={(v) => setPump((a) => upd(a, i, { durationH: num(v) }))} />
                    <FTxt label="q" w={52} v={s.specificCapacity} set={(v) => setPump((a) => upd(a, i, { specificCapacity: num(v) }))} />
                    <button onClick={() => setPump((a) => del(a, i))} style={xbtn}>×</button>
                  </div>
                ))}
                <button onClick={() => setPump((a) => [...a, { id: null, stageNo: a.length + 1, qM3h: null, drawdownSM: null, durationH: null, specificCapacity: null, remarks: null }])} style={addBtn}>+ stopień</button>
              </Sec>

              <Sec id="water" title={`Jakość wody (${draft.water?.values.length ?? 0})`}>
                <Stack>
                  <FDte label="Data poboru" fid="water.sampledDate" v={draft.water?.sampledDate ?? null} set={(v) => setWater((w) => ({ ...(w ?? emptySample()), sampledDate: v }))} />
                  <FTxt label="Laboratorium" v={draft.water?.labName ?? null} set={(v) => setWater((w) => ({ ...(w ?? emptySample()), labName: v }))} />
                  {(draft.water?.values ?? []).map((v, i) => (
                    <div key={v.id ?? 'v' + i} style={rowLine}>
                      <FTxt label="parametr" fid={`waterval.${i}.name`} v={v.paramName} set={(x) => setWater((w) => w ? { ...w, values: upd(w.values, i, { paramName: x }) } : w)} />
                      <FTxt label="jedn." w={72} v={v.unit} set={(x) => setWater((w) => w ? { ...w, values: upd(w.values, i, { unit: x }) } : w)} />
                      <FTxt label="wart." w={64} v={v.valueText} set={(x) => setWater((w) => w ? { ...w, values: upd(w.values, i, { valueText: x }) } : w)} />
                      <button onClick={() => setWater((w) => w ? { ...w, values: del(w.values, i) } : w)} style={xbtn}>×</button>
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                    <SearchableSelect value="" onChange={addParamRow} options={paramOpts} placeholder="+ parametr ze słownika" searchThreshold={6} />
                    <button onClick={() => setWater((w) => { const b = w ?? emptySample(); return { ...b, values: [...b.values, { id: null, parameterId: null, paramName: '', unit: null, valueText: null, sortOrder: (b.values.length + 1) * 10 }] }; })} style={addBtn}>+ pusty</button>
                  </div>
                </Stack>
              </Sec>
              </SecCtx.Provider>
            </div>
          )}
        </div>
      </div>

      {review && (
        <ExtractionReviewModal
          extraction={review}
          onCancel={() => setReview(null)}
          onConfirm={onReviewConfirm}
        />
      )}
    </div>
  );
}

/**
 * Nakładka „AI odczytuje dokument" — pasek postępu (asymptotyczny, dochodzi do ~96% i czeka na wynik),
 * licznik czasu w sekundach oraz opis bieżącego etapu (zmienia się z upływem czasu). Ponieważ odczyt to
 * jedno żądanie blokujące (bez strumienia), postęp jest szacowany po czasie, a nie zgłaszany z serwera.
 */
function AiExtractOverlay({ elapsed, fileName, uploadPct }:
    { elapsed: number; fileName: string | null; uploadPct?: number | null }) {
  // Dopóki trwa wysyłka pliku (uploadPct < 100) pokazujemy REALNY postęp uploadu; po jej
  // zakończeniu przełączamy się na asymptotyczną animację czasu przetwarzania po stronie AI.
  const uploading = uploadPct != null && uploadPct < 100;
  const phase = uploading ? `Wysyłanie pliku do AI… ${uploadPct}%`
    : elapsed < 2 ? 'Wysyłanie pliku do AI…'
    : elapsed < 6 ? 'AI analizuje dokument…'
      : elapsed < 14 ? 'Odczytywanie danych (warstwy, konstrukcja, pompowania, wyniki wody)…'
        : elapsed < 30 ? 'Model bywa chwilowo obciążony — czekam na odpowiedź…'
          : 'Wciąż przetwarzam… przy dużych dokumentach lub obciążeniu AI może to potrwać dłużej.';
  // Asymptotyczne wypełnienie: szybkie na starcie, zwalnia i zatrzymuje ~96% aż do zakończenia żądania.
  const pct = uploading ? uploadPct : Math.min(96, 100 * (1 - Math.exp(-elapsed / 9)));
  const secs = elapsed.toFixed(1).replace('.', ',');
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 40, background: 'rgba(255,255,255,0.86)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: 'min(460px, 92%)', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, boxShadow: '0 12px 40px rgba(15,23,42,0.18)', padding: '22px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <span className="inline-block w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" style={{ color: '#f59e0b' }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>Odczyt danych z dokumentu (AI)</div>
          <div style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums', fontSize: 14, color: '#b45309', fontWeight: 700 }}>{secs} s</div>
        </div>
        {fileName && (
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Plik: {fileName}
          </div>
        )}
        <div style={{ height: 8, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ height: '100%', width: pct + '%', background: 'linear-gradient(90deg,#f59e0b,#f97316)', borderRadius: 999, transition: 'width 0.2s ease' }} />
        </div>
        <div style={{ fontSize: 13, color: '#334155', minHeight: 20 }}>{phase}</div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 10 }}>
          Nie zamykaj tego okna — po odczycie pokażemy dane do sprawdzenia i zatwierdzenia.
        </div>
      </div>
    </div>
  );
}

// ---------- małe komponenty pól ----------
/** Rozwijana sekcja (sterowana przez kontekst — klik w podgląd może ją otworzyć). */
function Sec({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  const { openSet, toggle } = useContext(SecCtx);
  return (
    <details open={openSet.has(id)} data-sec={id}
      onToggle={(e) => toggle(id, (e.currentTarget as HTMLDetailsElement).open)}
      style={{ marginBottom: 8, border: '1px solid #e2e8f0', borderRadius: 6 }}>
      <summary style={{ cursor: 'pointer', fontWeight: 600, padding: '6px 8px', background: '#f8fafc', borderRadius: 6 }}>{title}</summary>
      <div style={{ padding: 8 }}>{children}</div>
    </details>
  );
}
/** Pionowy stos pól z odstępem na unoszące się etykiety. */
function Stack({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gap: 12 }}>{children}</div>;
}
/** Wiersz z trwałą etykietą po lewej — dla checkboxów/akcji (nie unosi etykiety). */
function Fld({ l, children }: { l: string; children: React.ReactNode }) {
  return <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
    <span style={{ width: 110, minWidth: 110, color: '#475569' }}>{l}</span>{children}
  </div>;
}

const rowLine: React.CSSProperties = { display: 'flex', gap: 4, alignItems: 'center' };
const levelRow: React.CSSProperties = { display: 'flex', gap: 4, alignItems: 'center', marginBottom: 12 };

// Pole z „unoszącą się" etykietą: pełni rolę placeholdera w środku, a po wpisaniu/fokusie
// przesuwa się nad ramkę — dzięki czemu etykieta pozostaje widoczna także po wpisaniu wartości.
const fInp: React.CSSProperties = { width: '100%', fontSize: 12, padding: '7px 7px 4px', border: '1px solid #cbd5e1', borderRadius: 4, minWidth: 0, boxSizing: 'border-box', background: 'transparent' };
function fLabel(up: boolean, focused: boolean): React.CSSProperties {
  return {
    position: 'absolute', left: 6, top: up ? -6 : '50%', transform: up ? 'none' : 'translateY(-50%)',
    pointerEvents: 'none', background: '#fff', padding: '0 3px', lineHeight: 1,
    fontSize: up ? 9.5 : 12, color: focused ? '#0891b2' : (up ? '#64748b' : '#94a3b8'),
    maxWidth: 'calc(100% - 8px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    transition: 'top .12s ease, font-size .12s ease, color .12s ease, transform .12s ease',
  };
}
const wrapStyle = (w?: number): React.CSSProperties =>
  ({ position: 'relative', minWidth: 0, ...(w ? { width: w, flex: 'none' } : { flex: '1 1 0%' }) });

function FTxt({ label, v, set, w, fid }: { label: string; v: unknown; set: (v: string | null) => void; w?: number; fid?: string }) {
  const [f, setF] = useState(false);
  const val = S(v);
  const up = f || val !== '';
  return (
    <div style={wrapStyle(w)} data-fid={fid}>
      <input value={val} title={label} onFocus={() => setF(true)} onBlur={() => setF(false)}
        onChange={(e) => set(e.target.value === '' ? null : e.target.value)} style={fInp} />
      <label style={fLabel(up, f)}>{label}</label>
    </div>
  );
}
function FArea({ label, v, set, fid }: { label: string; v: unknown; set: (v: string | null) => void; fid?: string }) {
  const [f, setF] = useState(false);
  const val = S(v);
  const up = f || val !== '';
  return (
    <div style={wrapStyle()} data-fid={fid}>
      <textarea value={val} rows={2} title={label} onFocus={() => setF(true)} onBlur={() => setF(false)}
        onChange={(e) => set(e.target.value === '' ? null : e.target.value)} style={{ ...fInp, paddingTop: 9, resize: 'vertical' }} />
      <label style={{ ...fLabel(up, f), top: up ? -6 : 9, transform: 'none' }}>{label}</label>
    </div>
  );
}
function FDte({ label, v, set, w, fid }: { label: string; v: string | null; set: (v: string | null) => void; w?: number; fid?: string }) {
  const [f, setF] = useState(false);
  return (
    <div style={wrapStyle(w)} data-fid={fid}>
      <input type="date" value={v ?? ''} title={label} onFocus={() => setF(true)} onBlur={() => setF(false)}
        onChange={(e) => set(e.target.value || null)} style={{ ...fInp, paddingTop: 9 }} />
      <label style={{ ...fLabel(true, f), top: -6, transform: 'none' }}>{label}</label>
    </div>
  );
}
function FSelect({ label, value, onChange, options, w, fid, searchThreshold }: {
  label: string; value: string; onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>; w?: number; fid?: string; searchThreshold?: number;
}) {
  const [f, setF] = useState(false);
  // unoś etykietę, gdy select pokazuje etykietę WYBRANEJ opcji (także opcji o pustej wartości, np.
  // „Domyślna firmy (auto)" / „—"); tylko przy braku dopasowania (placeholder „ ") zostaje jako placeholder
  const up = f || options.some((o) => o.value === value);
  return (
    <div style={wrapStyle(w)} data-fid={fid}
      onFocusCapture={() => setF(true)}
      onBlurCapture={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setF(false); }}>
      <SearchableSelect value={value} onChange={onChange} options={options} placeholder=" " searchThreshold={searchThreshold} title={label} />
      <label style={fLabel(up, f)}>{label}</label>
    </div>
  );
}
const emptySample = (): NonNullable<BoreholeCardModel['water']> => ({ id: null, sampledDate: null, labName: null, examiner: null, remarks: null, values: [] });

const rowBox: React.CSSProperties = { border: '1px solid #eef2f7', borderRadius: 4, padding: '10px 6px 6px', marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 10 };
const xbtn: React.CSSProperties = { background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 4, width: 22, cursor: 'pointer' };
const addBtn: React.CSSProperties = { background: '#e0f2fe', color: '#075985', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 12, cursor: 'pointer' };
function btn(bg: string): React.CSSProperties {
  return { background: bg, color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 13, cursor: 'pointer' };
}

function btnOutline(color: string): React.CSSProperties {
  return { background: '#fff', color, border: `1px solid ${color}`, borderRadius: 6, padding: '5px 12px', fontSize: 13, cursor: 'pointer' };
}

/** Zgrupowane akcje eksportu karty (PDF / SVG / zapis do systemu) w jednym rozwijanym menu. */
function ExportMenu({ disabled, onPdf, onSvg, onSaveToSystem }: {
  disabled?: boolean;
  onPdf: () => void;
  onSvg: () => void;
  onSaveToSystem: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const item: React.CSSProperties = {
    display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none',
    padding: '8px 12px', fontSize: 13, color: '#0f172a', cursor: 'pointer', whiteSpace: 'nowrap',
  };
  const pick = (fn: () => void) => { setOpen(false); fn(); };
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} disabled={disabled} style={btn('#0369a1')}>Eksport ▾</button>
      {open && !disabled && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 24px rgba(15,23,42,0.14)', zIndex: 30, minWidth: 200, overflow: 'hidden' }}>
          <button style={item} onClick={() => pick(onPdf)}>Pobierz PDF</button>
          <button style={item} onClick={() => pick(onSvg)}>Pobierz SVG (źródło)</button>
          <div style={{ borderTop: '1px solid #f1f5f9' }} />
          <button style={item} onClick={() => pick(onSaveToSystem)}>Zapisz PDF do systemu</button>
        </div>
      )}
    </div>
  );
}

// ---------- mapowanie draft → BoreholeCardRequest ----------
function toRequest(d: BoreholeCardModel) {
  const p = d.presentation;
  return {
    designation: d.designation, function: d.function, status: d.status,
    totalDepth: d.totalDepth, diameterMm: d.diameterMm, yieldM3h: d.yieldM3h,
    latitude: d.latitude, longitude: d.longitude, yearCompleted: d.yearCompleted,
    elevationMNpm: d.elevationMNpm, workStartDate: d.workStartDate, workEndDate: d.workEndDate,
    drillingSystem: d.drillingSystem, drillingTools: d.drillingTools, drillingCourse: d.drillingCourse,
    sampleMethod: d.sampleMethod, sampleStorage: d.sampleStorage, remarks: d.remarks,
    presentation: {
      cardType: p.cardType, hiddenColumns: p.hiddenColumns, showLogo: p.showLogo, companyId: p.companyId,
      attachmentLabel: p.attachmentLabel, templateFamily: p.templateFamily, titleVariant: p.titleVariant,
      mapScale: p.mapScale, depthScale: p.depthScale, casingStickupM: p.casingStickupM,
      coordinateSystem: p.coordinateSystem,
      documentingGeologistId: p.documentingGeologistId, documentingGeologistText: p.documentingGeologistText,
      coSignerText: p.coSignerText,
      kValueMs: p.kValueMs, kValueMh: p.kValueMh, kFormula: p.kFormula, kFromSieve: p.kFromSieve,
      rRangeM: p.rRangeM, rFormula: p.rFormula, qExploatacyjneM3h: p.qExploatacyjneM3h,
      qDopFiltraM3h: p.qDopFiltraM3h, scTotalM: p.scTotalM, hydroNotes: p.hydroNotes,
      projectedDepthM: p.projectedDepthM, drillingPurpose: p.drillingPurpose,
      eruptionHazardClass: p.eruptionHazardClass, h2sHazardCategory: p.h2sHazardCategory,
      rigInfo: p.rigInfo, supervisionNotes: p.supervisionNotes, sourceProjectRef: p.sourceProjectRef,
      mudType: p.mudType, bitType: p.bitType, drillPressure: p.drillPressure,
      drillRpm: p.drillRpm, mudFlow: p.mudFlow, plannedTests: p.plannedTests,
      showTitleBlock: p.showTitleBlock, drawingTitle: p.drawingTitle, checkedByText: p.checkedByText,
    },
    layers: d.layers, waterLevels: d.waterLevels, casing: d.casing, pumping: d.pumping, water: d.water,
    liquidation: d.liquidation, revisions: d.revisions,
  };
}
